if (!customElements.get("component-product-card")) {
  class ComponentProductCard extends HTMLElement {
    constructor() {
      super();
      // Ne cible que les swatches variantes (avec data-variant-id),
      // pour ne pas interférer avec le switch-colors (ancres sans data-variant-id)
      this.swatches = this.querySelectorAll(".colorSwatchContent[data-variant-id]");
      this.inputs = this.querySelectorAll("[data-input]");
      this.variantSchema = Array.from(this.querySelectorAll("script")).map((script) => JSON.parse(script.textContent));
      this.previewTimer = null;
      this.snapshot = null;
    }

    connectedCallback() {
      this.listenEvents();
    }

    // disconnetedCallback() {
    //     this.removeEvents();
    // }

    listenEvents() {
      this.swatches.forEach((swatch) => {
        swatch.addEventListener("click", (event) => {
          this.handleClick(event);
        });
      });

      // Interception des clics sur le switch-colors rendu dans la carte
      const linkedProductSwatches = this.querySelectorAll(
        ".product-switch-color-colors .colorSwatchContent"
      );
      linkedProductSwatches.forEach((anchor) => {
        anchor.addEventListener("click", (event) => {
          // Empêche la navigation seulement dans le contexte de la carte
          event.preventDefault();
          const href = anchor.getAttribute("href");
          if (!href) return;
          this.startPreviewFromUrl(href, anchor);
        });
      });
    }

    removeEvents() {
      this.swatches.forEach((swatch) => {
        swatch.removeEventListener("click", (event) => {
          this.handleClick(event);
        });
      });
    }

    handleClick(event) {
      const swatch = event.target;
      const swatchId = swatch.closest(".colorSwatch").getAttribute("data-id");
      const image = event.target.dataset.variantImg;
      const price = event.target.dataset.variantPrice;
      const variantId = event.target.dataset.variantId;

      this.updateSwatches(swatchId);
      this.updateImage(image);
      this.updatePrice(price);
      this.updateUrl(variantId);

      // Update price and compare_at_price
      this.variantSchema.forEach((variant) => {
        if (variant.id.toString() === variantId) {
          if (variant.compare_at_price) {
            this.updateCompareAtPrice(variant.compare_at_price);
          }
        }
      });
    }

    async startPreviewFromUrl(productUrl, clickedAnchor) {
      // Nettoie timer précédent si existant
      if (this.previewTimer) {
        clearTimeout(this.previewTimer);
        this.previewTimer = null;
      }

      // Snapshot initial si pas déjà pris
      if (!this.snapshot) {
        this.snapshot = this.takeSnapshot();
      }

      // Capturer le swatch sélectionné d'origine si disponible
      if (!this._originalLinkedSelected) {
        this._originalLinkedSelected = this.querySelector(
          ".product-switch-color-colors .colorSwatch.selected"
        );
      }

      // Si on clique sur le swatch original, restaure immédiatement
      const clickedSwatch = clickedAnchor.closest(".colorSwatch");
      if (clickedSwatch === this._originalLinkedSelected) {
        this.restoreSnapshot();
        return;
      }

      // Mettre à jour l'état visuel de sélection sur les swatches liés
      this.updateLinkedSwatchSelection(clickedAnchor);

      try {
        const jsonUrl = productUrl.endsWith(".json") ? productUrl : `${productUrl}.json`;
        const response = await fetch(jsonUrl, { credentials: "same-origin" });
        if (!response.ok) throw new Error("Fetch produit lié échoué");
        const data = await response.json();
        const product = data.product || data; // compat

        // Déterminer image
        let imageSrc = null;
        if (product?.images?.length) {
          imageSrc = product.images[0].src || product.images[0];
        } else if (product?.image?.src) {
          imageSrc = product.image.src;
        } else if (product?.featured_image) {
          imageSrc = product.featured_image;
        }

        // Déterminer variant pour prix
        let variant = null;
        if (Array.isArray(product?.variants) && product.variants.length) {
          variant = product.variants.find((v) => v.available) || product.variants[0];
        }

        // Appliquer aperçu
        if (imageSrc) {
          this.updateImage(imageSrc);
        }
        // Récupérer le titre court depuis l'attribut title-court de l'ancre cliquée
        // Si vide, utiliser le titre du produit depuis le JSON
        const titleCourt = clickedAnchor.getAttribute("title-court");
        const productTitle = (titleCourt && titleCourt.trim() !== "")
          ? titleCourt
          : (product?.title || "");
        this.updateTitleAndLinks(productTitle, productUrl);
        // Note: updateBadge désactivé car les données nécessaires ne sont pas disponibles dans le JSON par défaut
        // this.updateBadge(product);

        if (variant) {
          const formattedPrice = this.formatMoney(variant.price);
          this.updatePrice(formattedPrice);

          if (variant.compare_at_price && Number(variant.compare_at_price) > Number(variant.price)) {
            const formattedCompare = this.formatMoney(variant.compare_at_price);
            this.updateCompareAtPrice(formattedCompare);
          } else {
            // Si pas de compare, vider visuellement
            this.updateCompareAtPrice("");
          }
        }

        // Timer de restauration (7s)
        this.previewTimer = setTimeout(() => {
          this.restoreSnapshot();
        }, 7000);
      } catch (e) {
        // En cas d'erreur, on ne casse pas la navigation au prochain clic
        console.error(e);
      }
    }

    updateLinkedSwatchSelection(clickedAnchor) {
      // Retire selected de tous les swatches liés, puis met selected sur celui cliqué
      const allLinked = this.querySelectorAll(
        ".product-switch-color-colors .colorSwatch"
      );
      allLinked.forEach((sw) => {
        sw.classList.remove("selected");
      });
      const parent = clickedAnchor.closest(".colorSwatch");
      if (parent) {
        parent.classList.add("selected");
      }
    }

    takeSnapshot() {
      const img = this.querySelector("img");
      const mediaLink = this.querySelector("a.card__media");
      const contentLink = this.querySelector("a.card__content");
      const titleAnchor = this.querySelector('[id^="CardLink-"]');
      const quickAddRedirect = this.querySelector(".quick-add__submit-redirect");
      const priceContainers = Array.from(this.querySelectorAll(".price-item"));
      const compareContainers = Array.from(this.querySelectorAll(".--price-item--regular"));
      const badgeContainers = Array.from(this.querySelectorAll(".card__badge"));

      return {
        imgSrc: img ? img.getAttribute("src") : null,
        imgSrcset: img ? img.getAttribute("srcset") : null,
        mediaHref: mediaLink ? mediaLink.getAttribute("href") : null,
        contentHref: contentLink ? contentLink.getAttribute("href") : null,
        titleText: titleAnchor ? titleAnchor.textContent : null,
        titleHref: titleAnchor ? titleAnchor.getAttribute("href") : null,
        quickHref: quickAddRedirect ? quickAddRedirect.getAttribute("href") : null,
        prices: priceContainers.map((el) => el.textContent),
        compares: compareContainers.map((el) => el.textContent),
        badges: badgeContainers.map((el) => el.innerHTML),
      };
    }

    restoreSnapshot() {
      if (!this.snapshot) return;
      const img = this.querySelector("img");
      const mediaLink = this.querySelector("a.card__media");
      const contentLink = this.querySelector("a.card__content");
      const titleAnchor = this.querySelector('[id^="CardLink-"]');
      const quickAddRedirect = this.querySelector(".quick-add__submit-redirect");
      const priceContainers = Array.from(this.querySelectorAll(".price-item"));
      const compareContainers = Array.from(this.querySelectorAll(".--price-item--regular"));
      const badgeContainers = Array.from(this.querySelectorAll(".card__badge"));

      if (img && this.snapshot.imgSrc) {
        img.src = this.snapshot.imgSrc;
        if (this.snapshot.imgSrcset) img.srcset = this.snapshot.imgSrcset;
      }
      if (mediaLink && this.snapshot.mediaHref) mediaLink.setAttribute("href", this.snapshot.mediaHref);
      if (contentLink && this.snapshot.contentHref) contentLink.setAttribute("href", this.snapshot.contentHref);
      if (titleAnchor && this.snapshot.titleHref) {
        titleAnchor.setAttribute("href", this.snapshot.titleHref);
        if (this.snapshot.titleText != null) titleAnchor.textContent = this.snapshot.titleText;
      }
      if (quickAddRedirect && this.snapshot.quickHref) quickAddRedirect.setAttribute("href", this.snapshot.quickHref);
      priceContainers.forEach((el, i) => {
        if (this.snapshot.prices[i] != null) el.textContent = this.snapshot.prices[i];
      });
      compareContainers.forEach((el, i) => {
        if (this.snapshot.compares[i] != null) el.textContent = this.snapshot.compares[i];
      });
      badgeContainers.forEach((el, i) => {
        if (this.snapshot.badges[i] != null) el.innerHTML = this.snapshot.badges[i];
      });

      this.snapshot = null;
      if (this.previewTimer) {
        clearTimeout(this.previewTimer);
        this.previewTimer = null;
      }

      // Restaure l'état de sélection des swatches liés
      const allLinked = this.querySelectorAll(
        ".product-switch-color-colors .colorSwatch"
      );
      allLinked.forEach((sw) => sw.classList.remove("selected"));
      if (this._originalLinkedSelected) {
        this._originalLinkedSelected.classList.add("selected");
      }
    }

    updateTitleAndLinks(title, href) {
      const mediaLink = this.querySelector("a.card__media");
      const contentLink = this.querySelector("a.card__content");
      const titleAnchor = this.querySelector('[id^="CardLink-"]');
      const quickAddRedirect = this.querySelector(".quick-add__submit-redirect");
      if (mediaLink) mediaLink.setAttribute("href", href);
      if (contentLink) contentLink.setAttribute("href", href);
      if (titleAnchor) {
        titleAnchor.setAttribute("href", href);
        if (title) titleAnchor.textContent = title;
      }
      if (quickAddRedirect) quickAddRedirect.setAttribute("href", href);
    }

    updateBadge(product) {
      const badgeContainers = this.querySelectorAll(".card__badge");
      if (!badgeContainers.length) return;

      // Logique similaire à celle dans card-product.liquid
      let badgeHTML = "";
      const allVariants = product?.variants || [];

      // Vérifier la disponibilité : plusieurs façons de déterminer si un variant est disponible
      // Si les champs ne sont pas dans le JSON, considérer comme disponible par défaut
      const isAvailable = allVariants.some((v) => {
        // Si aucun champ de disponibilité n'est présent, considérer comme disponible par défaut
        const hasAvailabilityInfo = v.available !== undefined ||
                                   v.inventory_policy !== undefined ||
                                   v.inventory_quantity !== undefined;

        if (!hasAvailabilityInfo) {
          // Pas d'info de disponibilité dans le JSON → disponible par défaut
          return true;
        }

        // Méthode 1: champ available explicite
        if (v.available === true) return true;
        // Méthode 2: inventory_policy = "continue" signifie toujours disponible
        if (v.inventory_policy === "continue") return true;
        // Méthode 3: inventory_quantity > 0
        if (v.inventory_quantity != null && v.inventory_quantity > 0) return true;
        // Méthode 4: available explicitement false → épuisé
        if (v.available === false) return false;
        // Méthode 5: inventory_policy = "deny" et inventory_quantity = 0 → épuisé
        if (v.inventory_policy === "deny" && v.inventory_quantity === 0) return false;

        // Par défaut, si on a des infos mais qu'elles ne disent pas explicitement "épuisé", considérer disponible
        return true;
      });

      const allVariantsPreorder =
        allVariants.length > 0 &&
        allVariants.every((v) => v.metafields?.custom?.preorder_date != null && v.metafields.custom.preorder_date !== "");

      // Récupérer les classes CSS depuis un badge existant dans la carte pour cohérence
      const existingBadgeSpan = badgeContainers[0].querySelector("span.badge");
      const baseClasses = existingBadgeSpan ? existingBadgeSpan.className : "badge badge--bottom-left";
      const badgeColorScheme = baseClasses.match(/color-(\S+)/)?.[1] || "sale_badge_color_scheme";

      // Chercher le texte traduit dans le DOM ou utiliser des fallbacks
      const findTranslation = (key, fallback) => {
        // Chercher dans window.theme.strings si disponible
        if (window.theme?.strings?.[key]) return window.theme.strings[key];
        // Chercher dans le DOM avec data-translate
        const el = document.querySelector(`[data-translate="${key}"]`);
        if (el) return el.textContent.trim();
        return fallback;
      };

      // Vérifier les metafields de collection pour le badge
      // Dans Liquid: card_product.metafields.collection.badge ou card_product.metafields.collection.new_badge
      // Dans JSON, les metafields peuvent être structurées différemment
      // Essayer différentes structures possibles pour les metafields
      const collectionBadge =
        product?.metafields?.collection?.badge ||
        product?.metafields?.collection?.new_badge ||
        product?.metafields_global?.collection?.badge?.value ||
        product?.metafields_global?.collection?.new_badge?.value ||
        product?.metafields?.custom?.collection_badge?.value ||
        product?.metafields?.custom?.collection_new_badge?.value;

      // Badges de collection (metafields) - prend priorité si présent
      if (collectionBadge) {
        const badgeText = product.metafields?.collection?.new_badge ||
                         product.metafields?.collection?.badge ||
                         product.metafields_global?.collection?.new_badge?.value ||
                         product.metafields_global?.collection?.badge?.value ||
                         product.metafields?.custom?.collection_new_badge?.value ||
                         product.metafields?.custom?.collection_badge?.value ||
                         collectionBadge;
        badgeHTML = `<span class="${baseClasses}">${badgeText}</span>`;
      } else if (!isAvailable) {
        // Produit épuisé seulement si aucun variant disponible
        const soldOutText = findTranslation("products.product.sold_out", "Épuisé");
        badgeHTML = `<span class="${baseClasses.replace(/color-\S+/, "")} color-sold_out_badge_color_scheme">${soldOutText}</span>`;
      } else if (allVariantsPreorder) {
        // Précommande si tous les variants sont en précommande
        const preorderText = findTranslation("products.product.preorder_badge", "Précommande");
        badgeHTML = `<span class="${baseClasses}">${preorderText}</span>`;
      } else {
        // Produit disponible, pas de badge spécial - vider le badge (sera restauré par le snapshot)
        badgeHTML = "";
      }

      badgeContainers.forEach((badgeContainer) => {
        badgeContainer.innerHTML = badgeHTML;
      });
    }

    formatMoney(raw) {
      const cents = this.toCents(raw);
      if (cents == null) return "";
      if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
        return window.Shopify.formatMoney(cents, window.theme?.moneyFormat || "{{amount_no_decimals_with_comma_separator}} €");
      }
      return new Intl.NumberFormat(undefined, { style: "currency", currency: "EUR" }).format(cents / 100);
    }

    toCents(raw) {
      if (raw == null) return null;
      // Strings comme "55.00" => unités
      if (typeof raw === "string") {
        const cleaned = raw.replace(",", ".").replace(/[^0-9.\-]/g, "");
        const num = parseFloat(cleaned);
        if (isNaN(num)) return null;
        return Math.round(num * 100);
      }
      // Nombres: heuristique - si < 1000 on suppose unités, sinon centimes
      if (typeof raw === "number") {
        if (!isFinite(raw)) return null;
        if (raw < 1000) return Math.round(raw * 100);
        return Math.round(raw);
      }
      return null;
    }

    updateUrl(variantId) {
      const href = this.querySelector(".quick-add__submit-redirect").getAttribute("href");

      if (href.includes("variant")) {
        const newHref = href.replace(/variant=\d+/, `variant=${variantId}`);
        this.querySelector(".quick-add__submit-redirect").setAttribute("href", newHref);
      } else {
        const newHref = `${href}?variant=${variantId}`;
        this.querySelector(".quick-add__submit-redirect").setAttribute("href", newHref);
      }
    }

    updateSwatches(currentSwatchId) {
      this.querySelectorAll(".product-card__swatch").forEach((swatch) => {
        if (swatch.getAttribute("id") === currentSwatchId) {
          swatch.classList.add("active");
        } else {
          swatch.classList.remove("active");
        }
      });
    }

    updateCompareAtPrice(compareAtPrice) {
      const compareAtPriceContainers = this.querySelectorAll(".--price-item--regular");

      compareAtPriceContainers.forEach((compareAtPriceContainer) => {
        compareAtPriceContainer.innerText = compareAtPrice;
      });
    }

    updatePrice(price) {
      const priceContainers = this.querySelectorAll(".price-item");

      priceContainers.forEach((priceContainer) => {
        priceContainer.innerText = price;
      });
    }

    updateImage(image) {
      const img = this.querySelector("img");
      img.src = image;
      img.srcset = image;
    }
  }

  customElements.define("component-product-card", ComponentProductCard);
}
